import { useCallback, useEffect, useMemo, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { DesignPackageSummary } from "@/services/bedrock/dtos/DesignPackage";
import { designKeys } from "@/services/bedrock/queryKeys";
import { isInFlightStatus } from "../packageStatus";
import { DesignPackageChannelMessage, useDesignPackageChannel } from "./useDesignPackageChannel";

export const MAX_IN_FLIGHT_SUBSCRIPTIONS = 10;
const LIST_INVALIDATE_DEBOUNCE_MS = 500;

function newestFirst(a: DesignPackageSummary, b: DesignPackageSummary): number {
  return b.updatedAt.localeCompare(a.updatedAt);
}

/** Keeps in-flight list cards live by subscribing to up to 10 of their package channels, without toasts. */
export function useInFlightPackageChannels(pkgs: DesignPackageSummary[]): void {
  const queryClient = useQueryClient();
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const ids = useMemo(
    () =>
      pkgs
        .filter(pkg => isInFlightStatus(pkg.status))
        .sort(newestFirst)
        .slice(0, MAX_IN_FLIGHT_SUBSCRIPTIONS)
        .map(pkg => pkg.id),
    [pkgs]
  );

  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    []
  );

  const refreshLists = useCallback(() => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      timer.current = null;
      queryClient.invalidateQueries({ queryKey: designKeys.all });
    }, LIST_INVALIDATE_DEBOUNCE_MS);
  }, [queryClient]);

  // Card percent comes from the progress store, so only real changes and finished runs refetch the lists.
  const onMessage = useCallback(
    (message: DesignPackageChannelMessage) => {
      if (message.type === "updated") refreshLists();
      else if (message.type === "progress" && (message.status === "completed" || message.status === "failed"))
        refreshLists();
    },
    [refreshLists]
  );

  // A fixed number of hook calls keeps the hook order stable as the in-flight set changes.
  for (let slot = 0; slot < MAX_IN_FLIGHT_SUBSCRIPTIONS; slot++) {
    const id = ids[slot] ?? null;
    useDesignPackageChannel(id, { enabled: id != null, onMessage, onResync: refreshLists, toasts: false });
  }
}
